export const getNameFromValue = (options, value) => {
  if (!value || value === "") return "";
  const abbr = value?.abbr || value;
  const obj = options?.find((o) => o.abbr === abbr);
  if (!obj) return abbr;
  // If description is missing or empty, just show abbr
  if (!obj.description || obj.description === "") return abbr;
  return `${obj.description} (${obj.abbr})`;
};

export const isSameValue = (option, value) => {
  return value === option.abbr;
};

/**
 * Utility function to get a friendlier error message from a string.
 * @param {String} errorMsg
 * @returns {String} a friendlier error message
 */
export const getFriendlyMessage = (errorMsg = "") => {
  const friendlyMessagesMap = {
    duplicateKey: "Record already exists.",
    serverError:
      "There was a problem with the request. Please try different input.",
    deleted:
      "The record(s) that you are trying to change have been (soft) deleted.",
  };
  switch (true) {
    case /do not exist/i.test(errorMsg):
      return friendlyMessagesMap.deleted;
    case /bad request/i.test(errorMsg):
    case /internal server error/i.test(errorMsg):
      return friendlyMessagesMap.serverError;
    case /duplicatekey/i.test(errorMsg):
      return friendlyMessagesMap.duplicateKey;
    default:
      return errorMsg;
  }
};

/**
 * Groups and sorts form fields according to a layout configuration.
 *
 * @param {Array<Object>} fields - The array of field objects to be grouped and sorted. Each field should have a unique `field_name` property.
 * @param {Object} layoutProperties - The layout configuration object. Must contain a `groups` array, where each group defines its metadata and an ordered `fields` array (with field names or objects with a `name` property).
 *
 * @returns {Object} An object with:
 *   - groupedFields: Array of group objects, each containing group metadata and a `fields` array sorted according to the order in `layoutProperties.groups[].fields[]`.
 *   - sortedFields: Array containing the original input fields array (for legacy compatibility).
 *
 * @description
 * This function enables dynamic, declarative control over how form fields are grouped and ordered in the UI. By specifying the desired field order in `layoutProperties.groups[].fields[]`, you can ensure that fields are rendered in a predictable, business-driven sequence, regardless of their order in the input array. This is especially useful for generic form components that need to adapt to different entity types or layouts without hardcoding field arrangements. If `layoutProperties` or its `groups` property is missing, the function returns empty groups to avoid runtime errors.
 */
export const makeFieldGroupsByLayout = (fields, layoutProperties) => {
  if (!layoutProperties || !Array.isArray(layoutProperties.groups)) {
    return { groupedFields: [], sortedFields: [fields] };
  }
  // Return an array of group objects, each with group metadata and a fields array
  const groupedFields = layoutProperties.groups.map(group => {
    const groupFieldNames = (group.fields || []).map(gf => typeof gf === "string" ? gf : gf.name);
    // Sort fields according to the order in groupFieldNames
    const sortedFields = groupFieldNames
      .map(name => fields.find(f => f.field_name === name) || (name === "__placeholder__" ? { field_name: name } : null))
      .filter(Boolean); // Remove undefined if a name is not found
    return {
      ...group,
      fields: sortedFields
    };
  });
  return { groupedFields, sortedFields: [fields] };
}